'use client'

import * as Sentry from '@sentry/react'
import { useQueryClient } from '@tanstack/react-query'
import {
  IdTokenResult,
  User,
  getAuth,
  getIdToken,
  onAuthStateChanged,
  onIdTokenChanged,
  reload,
} from 'firebase/auth'
import posthog from 'posthog-js'
/* eslint-disable react-refresh/only-export-components */
import { FC, PropsWithChildren, createContext, useEffect, useRef, useState } from 'react'

import { signOutAction } from '@/redux/actions'
import { useAppDispatch } from '@/redux/hooks'

export type FirebaseUserContextState = {
  isLoading: boolean
  reloadUser(): Promise<void>
} & (
  | {
      decodedJwt?: IdTokenResult
      firebaseUser: User
      isLoggedIn: true
    }
  | { decodedJwt: undefined; firebaseUser: null; isLoggedIn: false }
)

export const FirebaseUserContext = createContext<FirebaseUserContextState>({
  decodedJwt: undefined,
  firebaseUser: null,
  isLoading: true,
  isLoggedIn: false,
  reloadUser: async () => {},
})

type Props = PropsWithChildren

export const FirebaseUserContextProvider: FC<Props> = ({ children }) => {
  const [firebaseUser, setFirebaseUser] = useState<User | null>(null)
  const [decodedJwt, setDecodedJwt] = useState<IdTokenResult>()
  const [isLoading, setIsLoading] = useState(true)
  const previousUser = useRef<User | null>(null)
  const previousFirebaseUser = useRef<User | null>(null)
  const queryClient = useQueryClient()
  const dispatch = useAppDispatch()

  /**
   * Tells Sentry which account an error belongs to.
   *
   * The Firebase UID and nothing else. `sendDefaultPii` stays off, and this deliberately
   * does not send the email or the personnummer: this app holds medical and social-care
   * records, and an error report is the last place either belongs. The uid is opaque on its
   * own and only resolves to a person through our own database.
   *
   * Keyed on the uid rather than hung off the sign-in/sign-out transitions below, so it is
   * also correct on a reload, where the session is restored without either firing.
   *
   * PostHog gets the same uid and nothing else. Its person profiles are permanent and
   * browsable, and every event the person ever sent joins onto them, so the no-email /
   * no-personnummer rule above matters more here than it does for a Sentry event.
   *
   * identify() only ever runs with a user. The teardown is not symmetric — see the
   * posthog.reset() in the sign-out branch below for why.
   */
  useEffect(() => {
    Sentry.setUser(firebaseUser ? { id: firebaseUser.uid } : null)

    if (firebaseUser) posthog.identify(firebaseUser.uid)
  }, [firebaseUser?.uid])

  useEffect(() => {
    const signedOut = previousFirebaseUser.current && !firebaseUser
    const signedIn = !previousFirebaseUser.current && firebaseUser

    if (signedIn) {
      console.log('signedIn')
      queryClient.removeQueries({
        queryKey: ['bankid-collect'],
      })
    }

    if (signedOut) {
      console.log('signedOut')
      queryClient.removeQueries()
      setDecodedJwt(undefined)
      dispatch(signOutAction())

      // PostHog persists the distinct_id in storage, so unlike Sentry.setUser(null) it
      // survives sign-out on its own: without this the next person at the same browser
      // keeps sending events as the account that just left. Shared workstations make that
      // a data problem, not just a reporting one.
      //
      // Deliberately here rather than in the uid effect above. That effect also runs on
      // every anonymous page load, and resetting there would throw away the anonymous
      // distinct_id each time — destroying the pre-login funnel continuity identify() is
      // there to preserve. Only an actual sign-out should clear it.
      posthog.reset()

      // Radix puts `pointer-events: none` on the body while a modal layer is open, and
      // takes it off again when that layer closes. Signing out unmounts the entire
      // authenticated tree — on mobile that includes the sidebar sheet the sign-out button
      // itself lives in — while it is still open, so the restore never runs.
      //
      // What is left is a login page that renders perfectly and ignores every tap: hit
      // testing falls straight through to <html>, because nothing under body can be hit.
      // Desktop escapes it only because the sidebar is a plain panel there rather than a
      // modal, which is why it looked like a mobile layout bug.
      //
      // Reset here rather than where the sheet lives: this is the one place that knows a
      // teardown is happening, so it covers whatever happened to be open when it did.
      document.body.style.pointerEvents = ''
      // ^ Issues with cookies in NextJS - disabled until we decide to fix it
      // setFirebaseCookie(undefined)
    }

    previousFirebaseUser.current = firebaseUser

    if (!firebaseUser) return

    let isMounted = true

    const updateIdToken = async () => {
      const { currentUser } = getAuth()

      if (!currentUser) return Promise.resolve(undefined)

      const decodedToken = await currentUser.getIdTokenResult()

      if (isMounted && decodedToken) {
        console.log('User was updated, setting new token')
        setDecodedJwt(decodedToken)
        // ^ Issues with cookies in NextJS - disabled until we decide to fix it
        // setFirebaseCookie(decodedToken.token, new Date(decodedToken.expirationTime))
      }
    }

    updateIdToken()

    return () => {
      isMounted = false
    }
  }, [firebaseUser])

  useEffect(() => {
    try {
      const auth = getAuth()

      const unsubAuthState = onAuthStateChanged(auth, user => {
        console.log('onAuthStateChanged()', user)
        previousUser.current = user
        setFirebaseUser(user)
        setIsLoading(false)
      })

      const unsubUserChanged = onIdTokenChanged(auth, async user => {
        console.log('onIdTokenChanged()', user)
        previousUser.current = user
        setFirebaseUser(user)
        setIsLoading(false)
      })

      console.log('Registered auth listeners')

      return () => {
        unsubAuthState()
        unsubUserChanged()
      }
    } catch (error) {
      console.error('Firebase initialization error:', error)
      setIsLoading(false)

      return
    }
  }, [])

  const reloadUser = async () => {
    let user = getAuth().currentUser
    if (!user) return
    await reload(user)
    await getIdToken(user, true)
    user = getAuth().currentUser ?? user
    // ^ Issues with cookies in NextJS - disabled until we decide to fix it
    // await setFirebaseCookie(token)
    previousUser.current = firebaseUser
    setFirebaseUser({ ...user })
  }

  const value: FirebaseUserContextState = firebaseUser
    ? {
        decodedJwt,
        firebaseUser,
        isLoading,
        isLoggedIn: true,
        reloadUser,
      }
    : {
        decodedJwt: undefined,
        firebaseUser: null,
        isLoading,
        isLoggedIn: false,
        reloadUser,
      }

  return <FirebaseUserContext.Provider value={value}>{children}</FirebaseUserContext.Provider>
}
